import type { CustomQueryPageParams, QueryPageReturnType } from '@luna/api';
import { request } from '@luna/api';
import { deepClean } from '@luna/utils';
import { getEnv } from '@luna/env';
import { createQueryString } from '@luna/router';
import type { FinancingFilter } from '@api-client/accounts';

import type {
    FilterablePagedListResponse,
    Financing,
    FinancingsFilterOptions,
} from './types/aggregationService';

const DEFAULT_PAGE_NUMBER = 0;
const DEFAULT_PAGE_SIZE = 50;

type Embedded = FilterablePagedListResponse<Financing, FinancingFilter>['_embedded'];

type FetchFinancingListPage = (
    queryParams: CustomQueryPageParams<FinancingsFilterOptions>,
    options?: { signal?: AbortSignal },
) => Promise<QueryPageReturnType<Financing, Embedded>>;

export const fetchFinancingListPage: FetchFinancingListPage = async (
    { pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_PAGE_SIZE, embed = [], ...rest },
    { signal } = {},
) => {
    const { apiGatewayUrl } = getEnv();

    const shouldNotFetchEmbeddedFilters =
        pageNumber !== DEFAULT_PAGE_NUMBER && embed.includes('filter');

    const embeddedParams = shouldNotFetchEmbeddedFilters
        ? embed.filter((embeddedParam) => embeddedParam !== 'filter')
        : embed;

    const queryParams = {
        page: pageNumber,
        size: pageSize,
        ...(embed.length !== DEFAULT_PAGE_NUMBER && { embed: embeddedParams.join(',') }),
        ...deepClean(rest),
    };

    const response = await request<FilterablePagedListResponse<Financing, FinancingFilter>>(
        `${apiGatewayUrl}product/public/financings${createQueryString(queryParams)}`,
        { signal },
    );

    const _embedded = response?._embedded;
    const unfilteredItemsCount = response?.unfilteredItemsCount;
    const additionalData = {
        ..._embedded,
        ...(unfilteredItemsCount && { unfilteredItemsCount }),
    };

    return {
        data: response.items,
        additionalData,
        pageNumber: response.page.number,
        pageCount: response.page.totalPages,
        pageSize: response.page.size,
        totalElements: response.page.totalElements,
    };
};
